import { useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, ImagePlus, Plus, Save, Trash2, X } from "lucide-react";

import { api, apiError } from "@/lib/api";

const emptyDraft = (event) => ({
  name: event.name,
  date: event.date,
  location: event.location,
  rings: event.rings,
  status: event.status,
  weight_class_type: event.weight_class_type || "amateur",
  combine_adult_ages: event.combine_adult_ages !== false,
  description: event.description || "",
  image_base64: event.image_base64 || "",
  registration_url: event.registration_url || "",
  registration_deadline: event.registration_deadline || "",
  calendar_published: !!event.calendar_published,
});

const emptyNew = { name: "", date: "", location: "", rings: 2, status: "draft", weight_class_type: "amateur", combine_adult_ages: true, description: "", image_base64: "", registration_url: "", registration_deadline: "", calendar_published: true };

export default function AdminCalendar() {
  const [events, setEvents] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState(emptyNew);

  const load = async () => {
    const response = await api.get("/events");
    setEvents(response.data);
    setDrafts(Object.fromEntries(response.data.map((event) => [event.id, emptyDraft(event)])));
  };

  useEffect(() => { load(); }, []);

  const patchDraft = (id, patch) => setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  const patchNew = (patch) => setNewDraft((current) => ({ ...current, ...patch }));

  const uploadFor = (setter, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setter(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async (id) => {
    setError("");
    setSavingId(id);
    try {
      const draft = drafts[id];
      await api.put(`/events/${id}`, {
        ...draft,
        rings: Number(draft.rings),
        image_base64: draft.image_base64 || null,
        registration_url: draft.registration_url || null,
        registration_deadline: draft.registration_deadline || null,
      });
      setNotice(`„${draft.name}“ wurde gespeichert.`);
      await load();
    } catch (requestError) {
      setError(apiError(requestError));
    } finally {
      setSavingId(null);
    }
  };

  const createEvent = async (submitEvent) => {
    submitEvent.preventDefault();
    setError("");
    setSavingId("new");
    try {
      await api.post("/events", {
        ...newDraft,
        rings: Number(newDraft.rings),
        image_base64: newDraft.image_base64 || null,
        registration_url: newDraft.registration_url || null,
        registration_deadline: newDraft.registration_deadline || null,
      });
      setNotice(`„${newDraft.name}“ wurde als Kalender-Eintrag angelegt.`);
      setNewDraft(emptyNew);
      setCreating(false);
      await load();
    } catch (requestError) {
      setError(apiError(requestError));
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (event) => {
    if (!window.confirm(`„${event.name}“ inklusive aller zugehörigen Turnierdaten (Kämpfer, Kategorien, Kämpfe) endgültig löschen?`)) return;
    setError("");
    setSavingId(event.id);
    try {
      await api.delete(`/events/${event.id}`);
      setNotice(`„${event.name}“ wurde gelöscht.`);
      await load();
    } catch (requestError) {
      setError(apiError(requestError));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="content-frame" data-testid="admin-calendar-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">SUPER ADMIN</p>
          <h1>KALENDER PFLEGEN</h1>
          <p className="muted">Neue Kalender-Einträge anlegen, Beitragsbild & Meldelink pflegen, veröffentlichen oder löschen.</p>
        </div>
        <button className="button primary" onClick={() => setCreating(!creating)} data-testid="admin-calendar-new-button">
          {creating ? <><X size={18} /> Abbrechen</> : <><Plus size={18} /> NEUER KALENDER-EINTRAG</>}
        </button>
      </header>
      {notice && <div className="success-notice" data-testid="admin-calendar-notice"><CheckCircle2 size={18} />{notice}</div>}
      {error && <div className="form-error" data-testid="admin-calendar-error">{error}</div>}

      {creating && (
        <form className="create-event-form" onSubmit={createEvent} data-testid="admin-calendar-new-form">
          <b>NEUER KALENDER-EINTRAG</b>
          <div className="form-grid">
            <label>Name<input required value={newDraft.name} onChange={(inputEvent) => patchNew({ name: inputEvent.target.value })} data-testid="admin-calendar-new-name" /></label>
            <label>Datum<input type="date" required value={newDraft.date} onChange={(inputEvent) => patchNew({ date: inputEvent.target.value })} data-testid="admin-calendar-new-date" /></label>
            <label>Ort<input required value={newDraft.location} onChange={(inputEvent) => patchNew({ location: inputEvent.target.value })} data-testid="admin-calendar-new-location" /></label>
            <label>Ringe<input type="number" min="1" max="12" required value={newDraft.rings} onChange={(inputEvent) => patchNew({ rings: inputEvent.target.value })} data-testid="admin-calendar-new-rings" /></label>
          </div>
          <div className="form-grid">
            <label>Turnier-Typ (Gewichtsklassen)
              <select value={newDraft.weight_class_type} onChange={(inputEvent) => patchNew({ weight_class_type: inputEvent.target.value })} data-testid="admin-calendar-new-type">
                <option value="amateur">Amateur (13 M / 10 W · z. B. -71 kg)</option>
                <option value="pro">Profi (17 M / 18 W · z. B. -72.58 kg)</option>
                <option value="kinder_jugend">Kinder & Jugend (U8–U18)</option>
                <option value="mixed">Gemischt (Hausgala · Regelwerk pro Kampf)</option>
              </select>
            </label>
            <label className="calendar-toggle">
              <input type="checkbox" checked={newDraft.combine_adult_ages} onChange={(inputEvent) => patchNew({ combine_adult_ages: inputEvent.target.checked })} data-testid="admin-calendar-new-combine-toggle" />
              <span>Erwachsene (Ü18) zusammenlegen</span>
            </label>
          </div>
          <div className="calendar-fields">
            <p className="eyebrow">KALENDER-BEITRAG</p>
            <label>Beschreibung<textarea rows={3} maxLength={500} value={newDraft.description} onChange={(inputEvent) => patchNew({ description: inputEvent.target.value })} placeholder="Kurzer Werbetext (max. 500 Zeichen)" data-testid="admin-calendar-new-description" /></label>
            <label>Meldelink (URL)<input type="url" value={newDraft.registration_url} onChange={(inputEvent) => patchNew({ registration_url: inputEvent.target.value })} placeholder="https://..." data-testid="admin-calendar-new-url" /></label>
            <label>Meldeschluss<input type="date" value={newDraft.registration_deadline} onChange={(inputEvent) => patchNew({ registration_deadline: inputEvent.target.value })} data-testid="admin-calendar-new-deadline" /></label>
            <label className="calendar-image-label">Beitragsbild
              <div className="calendar-image-picker">
                <input type="file" accept="image/*" onChange={(inputEvent) => uploadFor((value) => patchNew({ image_base64: value }), inputEvent.target.files?.[0])} data-testid="admin-calendar-new-image" />
                {!newDraft.image_base64 && <span><ImagePlus size={16} /> Bild auswählen</span>}
              </div>
            </label>
            {newDraft.image_base64 && <img className="calendar-upload-preview" src={newDraft.image_base64} alt="Vorschau" data-testid="admin-calendar-new-preview" />}
            <label className="calendar-toggle">
              <input type="checkbox" checked={newDraft.calendar_published} onChange={(inputEvent) => patchNew({ calendar_published: inputEvent.target.checked })} data-testid="admin-calendar-new-toggle" />
              <span>Im öffentlichen Eventkalender anzeigen</span>
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="button ghost" onClick={() => { setCreating(false); setNewDraft(emptyNew); }} data-testid="admin-calendar-new-cancel">Abbrechen</button>
            <button className="button primary" disabled={savingId === "new"} data-testid="admin-calendar-new-save"><Save size={17} /> {savingId === "new" ? "SPEICHERN …" : "EINTRAG ANLEGEN"}</button>
          </div>
        </form>
      )}

      {!events.length && !creating && (
        <div className="empty-state" data-testid="admin-calendar-empty">
          <CalendarDays size={32} />
          <h2>NOCH KEIN EINTRAG</h2>
          <p>Lege deinen ersten Kalender-Eintrag über den Button oben rechts an.</p>
        </div>
      )}

      <section className="admin-calendar-grid">
        {events.map((event) => {
          const draft = drafts[event.id];
          if (!draft) return null;
          return (
            <article className="admin-calendar-card" key={event.id} data-testid={`admin-calendar-item-${event.id}`}>
              <header>
                <div>
                  <p className="eyebrow">{draft.date} · {draft.location}</p>
                  <h2>{draft.name}</h2>
                </div>
                <span className={`status-pill ${draft.calendar_published ? "active" : ""}`} data-testid={`admin-calendar-status-${event.id}`}>{draft.calendar_published ? "VERÖFFENTLICHT" : "ENTWURF"}</span>
              </header>
              <label>Beschreibung
                <textarea rows={3} maxLength={500} value={draft.description} onChange={(inputEvent) => patchDraft(event.id, { description: inputEvent.target.value })} placeholder="Kurzer Werbetext für den Eventkalender." data-testid={`admin-calendar-description-${event.id}`} />
              </label>
              <label>Meldelink (URL)
                <input type="url" value={draft.registration_url} onChange={(inputEvent) => patchDraft(event.id, { registration_url: inputEvent.target.value })} placeholder="https://..." data-testid={`admin-calendar-url-${event.id}`} />
              </label>
              <label>Meldeschluss
                <input type="date" value={draft.registration_deadline} onChange={(inputEvent) => patchDraft(event.id, { registration_deadline: inputEvent.target.value })} data-testid={`admin-calendar-deadline-${event.id}`} />
              </label>
              <label>Turnier-Typ (Gewichtsklassen)
                <select value={draft.weight_class_type} onChange={(inputEvent) => patchDraft(event.id, { weight_class_type: inputEvent.target.value })} data-testid={`admin-calendar-type-${event.id}`}>
                  <option value="amateur">Amateur (13 M / 10 W)</option>
                  <option value="pro">Profi (17 M / 18 W)</option>
                  <option value="kinder_jugend">Kinder & Jugend (U8–U18)</option>
                  <option value="mixed">Gemischt (Hausgala)</option>
                </select>
              </label>
              <label className="calendar-toggle">
                <input type="checkbox" checked={draft.combine_adult_ages} onChange={(inputEvent) => patchDraft(event.id, { combine_adult_ages: inputEvent.target.checked })} data-testid={`admin-calendar-combine-${event.id}`} />
                <span>Erwachsene (Ü18) zusammenlegen</span>
              </label>
              <label className="calendar-image-label">Beitragsbild
                <div className="calendar-image-picker">
                  <input type="file" accept="image/*" onChange={(inputEvent) => uploadFor((value) => patchDraft(event.id, { image_base64: value }), inputEvent.target.files?.[0])} data-testid={`admin-calendar-image-${event.id}`} />
                  {!draft.image_base64 && <span><ImagePlus size={16} /> Bild auswählen</span>}
                </div>
              </label>
              {draft.image_base64 && <img className="calendar-upload-preview" src={draft.image_base64} alt="Vorschau" data-testid={`admin-calendar-preview-${event.id}`} />}
              <label className="calendar-toggle">
                <input type="checkbox" checked={draft.calendar_published} onChange={(inputEvent) => patchDraft(event.id, { calendar_published: inputEvent.target.checked })} data-testid={`admin-calendar-toggle-${event.id}`} />
                <span>Im öffentlichen Eventkalender anzeigen</span>
              </label>
              <div className="form-actions admin-calendar-actions">
                <button type="button" className="button ghost danger-button" onClick={() => remove(event)} disabled={savingId === event.id} data-testid={`admin-calendar-delete-${event.id}`}><Trash2 size={17} /> LÖSCHEN</button>
                <button type="button" className="button primary" onClick={() => save(event.id)} disabled={savingId === event.id} data-testid={`admin-calendar-save-${event.id}`}><Save size={17} /> {savingId === event.id ? "SPEICHERN …" : "SPEICHERN"}</button>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}
